/**
 * Global click-and-drag scrolling for rails/sliders, with light momentum.
 * Touch devices already get native swipe scrolling from the browser, so this
 * only engages for mouse/pen pointers. Any element can opt in via
 * [data-drag-scroll]; the selector list below covers the theme's known rails.
 */
(function () {
  'use strict';

  var DRAG_SELECTOR = [
    '.onlyfindz-home__category-rail',
    '[data-onlyfindz-rail]',
    '.marketplace-subnav__inner',
    '.product-tabs__list',
    '.thumbnail-slider .thumbnail-list',
    '.slider',
    '[data-drag-scroll]'
  ].join(', ');

  var DRAG_THRESHOLD = 4;
  var MAX_VELOCITY = 0.72;
  var FRICTION = 0.86;
  var MIN_VELOCITY = 0.03;
  var pointerFine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;

  var refreshers = [];

  function bindRailControls(rail) {
    if (!rail || rail.dataset.railControlsBound === 'true') return;

    var shell = rail.closest('.onlyfindz-home__rail-shell');
    var prev = shell && shell.querySelector('[data-onlyfindz-rail-prev]');
    var next = shell && shell.querySelector('[data-onlyfindz-rail-next]');

    if (!shell || !prev || !next) {
      rail.dataset.railControlsBound = 'true';
      return;
    }

    function items() {
      return Array.prototype.slice.call(rail.children || []);
    }

    function currentIndex() {
      var list = items();
      var closest = 0;
      var minDist = Infinity;

      list.forEach(function (item, index) {
        var dist = Math.abs(item.offsetLeft - rail.scrollLeft);
        if (dist < minDist) {
          minDist = dist;
          closest = index;
        }
      });

      return closest;
    }

    function goTo(index) {
      var list = items();
      var clamped = Math.max(0, Math.min(list.length - 1, index));
      var item = list[clamped];
      if (!item) return;
      item.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    }

    function updateState() {
      var maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      prev.disabled = rail.scrollLeft <= 4;
      next.disabled = rail.scrollLeft >= maxScrollLeft - 4;
    }

    refreshers.push(updateState);

    prev.addEventListener('click', function () {
      goTo(currentIndex() - 1);
    });

    next.addEventListener('click', function () {
      goTo(currentIndex() + 1);
    });

    rail.addEventListener('scroll', updateState, { passive: true });
    window.addEventListener('resize', updateState, { passive: true });
    updateState();
    rail.dataset.railControlsBound = 'true';
  }

  function getScrollAxis(el) {
    var overflowX = el.scrollWidth - el.clientWidth;
    var overflowY = el.scrollHeight - el.clientHeight;

    if (overflowY > overflowX && overflowY > 2) return 'y';
    if (overflowX > 2) return 'x';
    if (overflowY > 2) return 'y';
    return null;
  }

  function bindDragScroll(el) {
    if (!el || el.dataset.dragScrollBound === 'true') return;
    el.dataset.dragScrollBound = 'true';

    var pointerId = null;
    var dragging = false;
    var moved = false;
    var startX = 0;
    var startScrollLeft = 0;
    var lastX = 0;
    var lastT = 0;
    var velocity = 0;
    var rafId = null;
    var dragRafId = null;
    var targetScroll = 0;
    var axis = getScrollAxis(el);
    var scrollable = !!axis;
    var railLike = el.hasAttribute && el.hasAttribute('data-onlyfindz-rail');

    function refresh() {
      axis = getScrollAxis(el);
      scrollable = !!axis;
      if (pointerFine && !dragging) {
        el.style.cursor = scrollable ? 'grab' : '';
      }
    }
    refreshers.push(refresh);

    function cancelMomentum() {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }
    }

    function cancelDragFrame() {
      if (dragRafId !== null) {
        window.cancelAnimationFrame(dragRafId);
        dragRafId = null;
      }
    }

    function applyDragScroll() {
      dragRafId = null;
      if (axis === 'y') {
        el.scrollTop = targetScroll;
      } else {
        el.scrollLeft = targetScroll;
      }
    }

    function restoreNativeScrolling() {
      el.style.scrollSnapType = '';
      el.style.scrollBehavior = '';
    }

    function runMomentum() {
      if (Math.abs(velocity) < MIN_VELOCITY) {
        rafId = null;
        restoreNativeScrolling();
        return;
      }
      var before = axis === 'y' ? el.scrollTop : el.scrollLeft;
      var next = before - velocity * 16;
      if (axis === 'y') {
        el.scrollTop = next;
      } else {
        el.scrollLeft = next;
      }
      velocity *= FRICTION;
      if ((axis === 'y' ? el.scrollTop : el.scrollLeft) === before) {
        rafId = null;
        restoreNativeScrolling();
        return;
      }
      rafId = window.requestAnimationFrame(runMomentum);
    }

    function suppressNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
      el.removeEventListener('click', suppressNextClick, true);
    }

    function onPointerDown(event) {
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      if (event.button !== undefined && event.button !== 0) return;
      if (event.target.closest && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      refresh();
      if (!scrollable) return;

      cancelMomentum();
      cancelDragFrame();
      dragging = true;
      moved = false;
      pointerId = event.pointerId;
      startX = axis === 'y' ? event.clientY : event.clientX;
      startScrollLeft = axis === 'y' ? el.scrollTop : el.scrollLeft;
      lastX = startX;
      lastT = performance.now();
      velocity = 0;

      el.style.scrollSnapType = 'none';
      el.style.scrollBehavior = 'auto';
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
      el.classList.add('is-dragging');
    }

    function onPointerMove(event) {
      if (!dragging || event.pointerId !== pointerId) return;
      var currentX = axis === 'y' ? event.clientY : event.clientX;
      var delta = currentX - startX;
      if (!moved && Math.abs(delta) > DRAG_THRESHOLD) {
        moved = true;
        // Capture the pointer only once a real drag is confirmed. Captured
        // eagerly on pointerdown, the browser retargets the eventual
        // pointerup/click to this element instead of whatever was pressed
        // (a product card <a>, a tab, a thumbnail button) even on a plain
        // click with zero movement — silently eating every click inside any
        // rail with overflow content. Deferring it here keeps plain clicks
        // targeted at their real element while still giving genuine drags
        // reliable tracking once the cursor leaves the rail's bounds.
        try { el.setPointerCapture(pointerId); } catch (e) {}
      }
      targetScroll = startScrollLeft - delta;
      if (railLike) {
        applyDragScroll();
      } else if (dragRafId === null) {
        dragRafId = window.requestAnimationFrame(applyDragScroll);
      }

      var now = performance.now();
      var dt = now - lastT;
      if (dt > 0) {
        var v = (currentX - lastX) / dt;
        velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v));
      }
      lastX = currentX;
      lastT = now;
    }

    function endDrag(event) {
      if (!dragging) return;
      if (event && event.pointerId !== undefined && event.pointerId !== pointerId) return;
      dragging = false;
      cancelDragFrame();
      el.classList.remove('is-dragging');
      el.style.cursor = scrollable ? 'grab' : '';
      el.style.userSelect = '';

      if (pointerId !== null) {
        try { el.releasePointerCapture(pointerId); } catch (e) {}
      }
      pointerId = null;

      if (moved) {
        el.addEventListener('click', suppressNextClick, true);
        if (!railLike && Math.abs(velocity) > MIN_VELOCITY) {
          rafId = window.requestAnimationFrame(runMomentum);
          return;
        }
      }
      restoreNativeScrolling();
    }

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', endDrag);
    el.addEventListener('pointercancel', endDrag);
    el.addEventListener('pointerleave', function (event) {
      if (event.pointerType === 'mouse') endDrag(event);
    });
    el.addEventListener('dragstart', function (event) {
      if (scrollable) event.preventDefault();
    });

    refresh();
  }

  function scan(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(DRAG_SELECTOR).forEach(bindDragScroll);
    root.querySelectorAll('[data-onlyfindz-rail]').forEach(bindRailControls);
    if (root.nodeType === 1 && root.matches(DRAG_SELECTOR)) bindDragScroll(root);
    if (root.nodeType === 1 && root.matches('[data-onlyfindz-rail]')) bindRailControls(root);
  }

  function init() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) scan(node);
      });
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  var resizeTimer = null;
  window.addEventListener('resize', function () {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(function () {
      refreshers.forEach(function (fn) { fn(); });
    }, 150);
  }, { passive: true });
})();
